/**
 * ChallengeBanner — subtle CTA shown on /learning when a Community Challenge
 * is active. Fetches the active challenge on mount; renders nothing if none.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Trophy, ArrowRight, Users } from 'lucide-react';

const ChallengeBanner = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    axios.get(`${API}/freedriq/challenge/active`)
      .then((r) => {
        const c = r.data?.active;
        if (c && c.is_open) setChallenge(c);
      })
      .catch(() => { /* silent — no banner */ });
  }, [API]);

  if (!challenge) return null;

  return (
    <div
      className="block rounded-2xl border-2 border-primary/40 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 hover:border-primary/60 transition-colors cursor-pointer"
      data-testid="challenge-banner"
      role="button"
      tabIndex={0}
      onClick={() => navigate('/freedriq/challenge')}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/freedriq/challenge')}
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <Trophy className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-primary">FreezeDryIQ Community Challenge</div>
          <div className="font-heading font-extrabold text-sm truncate">{challenge.name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {challenge.description || 'Every operator, one leaderboard.'}
            <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold text-foreground">
              <Users className="w-3 h-3" /> {challenge.participants || 0}
            </span>
          </div>
        </div>
        <div className="text-primary flex-shrink-0">
          <ArrowRight className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-2 flex justify-end">
        <Link
          to="/freedriq/rankings"
          onClick={(e) => e.stopPropagation()}
          className="text-[11px] text-muted-foreground hover:text-primary hover:underline font-bold"
          data-testid="challenge-banner-rankings-link"
        >
          See rankings →
        </Link>
      </div>
    </div>
  );
};

export default ChallengeBanner;
